/**
 * Simulated Date Store (Pinia) — MODALITÀ DATA SIMULATA per le commesse
 *
 * @version 1.0.0
 * @description Strumento di collaudo riservato ai SUPER-ADMIN: permette di
 *              eseguire le azioni di workflow "come se" fosse un'altra data
 *              (mai antecedente all'ultima azione registrata sulla commessa —
 *              vincolo applicato dal BE, che risponde 422).
 *
 *              Il FE non calcola nulla: manda l'header `X-Simulated-Date`
 *              (interceptor axios in main.ts, SOLO sulle rotte /commesse e
 *              SOLO se super-admin) e il BE sposta il proprio orologio per la
 *              durata della request (Carbon::setTestNow). Qui si tiene solo la
 *              data scelta — persistita in sessionStorage così un reload non la
 *              perde ma una nuova scheda parte pulita — e il "today" da usare
 *              per le linee "oggi" di Gantt e timeline.
 */

import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

const STORAGE_KEY = 'commesse.simulatedDate'

/** Data locale (NON UTC) in formato Y-m-d: evita lo slittamento serale di toISOString(). */
export function localToday(): string {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

function restore(): string | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    return raw && /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : null
  } catch {
    return null
  }
}

export const useSimulatedDateStore = defineStore('simulatedDate', () => {
  /** Data simulata scelta (Y-m-d) oppure null = orologio reale. */
  const date = ref<string | null>(restore())

  /** Data/ora EFFETTIVA applicata dal BE nell'ultima risposta (header echo). */
  const effectiveAt = ref<string | null>(null)

  const isActive = computed(() => date.value !== null)

  /** "Oggi" da usare nel FE per linee/soglie: simulata se attiva, altrimenti reale. */
  const today = computed(() => date.value ?? localToday())

  function set(value: string | null): void {
    date.value = value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : null
    if (date.value === null) effectiveAt.value = null
    try {
      if (date.value) sessionStorage.setItem(STORAGE_KEY, date.value)
      else sessionStorage.removeItem(STORAGE_KEY)
    } catch { /* storage non disponibile: la modalità vive solo in memoria */ }
  }

  function clear(): void {
    set(null)
  }

  return { date, effectiveAt, isActive, today, set, clear }
})
